import { FirebaseServices } from "./config";
import { callFunction } from "./helpers";
import Constants from "expo-constants";
import {
  ISubscribeTopicRequest,
  ISubscribeTopicResponse,
  IUnsubscribeTopicRequest,
  IUnsubscribeTopicResponse,
  IMyTopicsResponse,
} from "common247";

/**
 * Resolves the real-time entity synchronization / update messaging WebSocket server URL.
 * Handles emulator host, production cloud functions, and web browser runtime.
 * Routes through the Express API path (`/api/realtimeWebSocketFunc`).
 * Returns null in local emulator environments where the Firebase Functions
 * emulator proxy does not support WebSocket upgrades.
 * @returns {string | null} The WebSocket endpoint URL or null in emulator/polling mode.
 */
export function getRealtimeWebSocketUrl(): string | null {
  const vars = Constants.expoConfig?.extra;
  const functionHost = vars?.FUNCTIONS_EMULATOR_HOST;

  // In local emulator mode, Firebase Functions emulator proxy does not support WebSocket upgrades.
  // Returning null allows useMessaging to use the resilient HTTP polling fallback without console errors.
  if (
    vars?.env === "LOCAL" ||
    (functionHost && (functionHost.includes("localhost") || functionHost.includes("127.0.0.1")))
  ) {
    return null;
  }

  if (typeof window !== "undefined" && window.location) {
    if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
      return null;
    }
  }

  // If FUNCTIONS_URL is configured (e.g. "https://europe-north1-memories-dev-247.cloudfunctions.net/api"),
  // convert http(s) protocol to ws(s) and append the function name.
  const functionsUrl: string | undefined = vars?.FUNCTIONS_URL || process.env.EXPO_PUBLIC_FUNCTIONS_URL;
  if (functionsUrl && functionsUrl.startsWith("http")) {
    const wsBaseUrl = functionsUrl
      .replace(/^http:\/\//i, "ws://")
      .replace(/^https:\/\//i, "wss://")
      .replace(/\/+$/, "");
    if (wsBaseUrl.endsWith("/api")) {
      return `${wsBaseUrl}/realtimeWebSocketFunc`;
    }
    return `${wsBaseUrl}/api/realtimeWebSocketFunc`;
  }

  const wsHost = vars?.MESSAGING_WS_HOST || "api-s6u6gpwjcq-lz.a.run.app";
  const isSecure = typeof window !== "undefined" && window.location ? window.location.protocol === "https:" : true;
  const protocol = isSecure ? "wss:" : "ws:";
  return `${protocol}//${wsHost}/api/realtimeWebSocketFunc`;
}

/**
 * Subscribes the user to one or more real-time entity topics via API.
 * @param fb Firebase services configuration
 * @param request ISubscribeTopicRequest payload
 * @returns Promise resolving to ISubscribeTopicResponse
 */
export async function subscribeTopicApi(
  fb: FirebaseServices,
  request: ISubscribeTopicRequest
): Promise<ISubscribeTopicResponse> {
  return await callFunction<ISubscribeTopicRequest, ISubscribeTopicResponse>(
    fb,
    "subscribeTopic",
    request
  );
}

/**
 * Unsubscribes the user from real-time entity topics via API.
 * @param fb Firebase services configuration
 * @param request IUnsubscribeTopicRequest payload
 * @returns Promise resolving to IUnsubscribeTopicResponse
 */
export async function unsubscribeTopicApi(
  fb: FirebaseServices,
  request: IUnsubscribeTopicRequest
): Promise<IUnsubscribeTopicResponse> {
  return await callFunction<IUnsubscribeTopicRequest, IUnsubscribeTopicResponse>(
    fb,
    "unsubscribeTopic",
    request
  );
}

/**
 * Retrieves all active and persistent topic subscriptions for the caller.
 * @param fb Firebase services configuration
 * @returns Promise resolving to IMyTopicsResponse
 */
export async function getMyTopicsApi(
  fb: FirebaseServices
): Promise<IMyTopicsResponse> {
  return await callFunction<Record<string, unknown>, IMyTopicsResponse>(
    fb,
    "getMyTopics",
    {}
  );
}
